import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import cities from "../data/tamilnadu-cities.json";
import {
  translateToTamil,
  filterCitiesWithTamilSupport,
} from "../helpers/citySearchHelper";

/**
 * CitySearchSelect
 * A searchable, mandatory city dropdown backed by tamilnadu-cities.json.
 * Supports both English and Tamil search with arrow key navigation.
 * When Tanglish mode is ON (typingMode === "tanglish"), the selected city
 * name is automatically transliterated to Tamil via the translation API.
 *
 * Props:
 *   value       – controlled value (city name string)
 *   onChange    – (cityName: string) => void
 *   onBlur      – () => void  (for Formik touch tracking)
 *   disabled    – boolean
 *   className   – class string for the input element
 *   placeholder – string
 */
const CitySearchSelect = ({
  value = "",
  onChange,
  onBlur,
  disabled = false,
  className = "",
  placeholder = "Search Tamil Nadu city…",
  noAutoTranslate = false,
}) => {
  const [query, setQuery] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const tamilCacheRef = useRef(new Map());
  const debounceTimeoutRef = useRef(null);
  const lastSearchQueryRef = useRef(null); // Track last search to avoid duplicates

  const isTranslatorEnabled = useSelector(
    (state) => (state.activePage.typingMode || "tanglish") === "tanglish",
  );

  // Sync display text when external value changes (e.g. form reset)
  useEffect(() => {
    setQuery(value || "");
    setHighlightedIndex(-1);
  }, [value]);

  // Filter cities with bilingual support
  const performSearch = useCallback(async (searchQuery) => {
    // Skip if search query hasn't actually changed
    if (searchQuery === lastSearchQueryRef.current) {
      return;
    }
    lastSearchQueryRef.current = searchQuery;

    if (searchQuery.trim().length === 0) {
      setFiltered(cities);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await filterCitiesWithTamilSupport(
        cities,
        searchQuery,
        tamilCacheRef.current,
        25
      );
      setFiltered(results);
    } catch (error) {
      console.error("Search error:", error);
      // Fallback to simple English search
      setFiltered(
        cities
          .filter((c) => c.toLowerCase().includes(searchQuery.toLowerCase()))
          .slice(0, 25)
      );
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      performSearch(query);
      setHighlightedIndex(-1);
    }, 150);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [query, performSearch]);

  useEffect(() => {
    performSearch(query);
  }, [performSearch]);

  // Close dropdown on outside click; revert if user typed but didn't pick
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        if (!cities.includes(query)) {
          setQuery(value || "");
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [query, value]);

  // Scroll highlighted row into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll("li[data-item]");
      items[highlightedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  const handleSelect = async (city) => {
    // Immediately close the dropdown and show the English name
    setQuery(city);
    onChange(city);
    setIsOpen(false);
    setHighlightedIndex(-1);

    // If Tanglish mode is ON, transliterate the city name to Tamil
    if (!isTranslatorEnabled || noAutoTranslate) return;

    setIsTranslating(true);
    try {
      const translated = await translateToTamil(city);
      if (translated && translated !== city) {
        setQuery(translated);
        onChange(translated);
      }
    } catch {
      // Silently keep the English city name on API failure
    } finally {
      setIsTranslating(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setIsOpen(true);
    setHighlightedIndex(-1);
    if (val === "") onChange("");
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
          handleSelect(filtered[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            setIsOpen(true);
            performSearch(query);
          }}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          placeholder={isTranslating ? "Translating…" : placeholder}
          disabled={disabled || isTranslating}
          className={`${className} no-translate`}
          autoComplete="off"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
        />
        {/* Translating spinner */}
        {isTranslating && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
            <svg
              className="h-4 w-4 animate-spin text-teal-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 5.373 0 0 12h4z"
              />
            </svg>
          </span>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl text-sm"
        >
          {isSearching ? (
            <li className="px-3.5 py-2.5 text-slate-400 italic flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full bg-teal-400 animate-pulse" />
              Searching...
            </li>
          ) : filtered.length > 0 ? (
            filtered.map((city, i) => (
              <li
                key={city}
                data-item
                role="option"
                aria-selected={i === highlightedIndex}
                onMouseDown={() => handleSelect(city)}
                className={`cursor-pointer px-3.5 py-2 font-medium transition-colors ${
                  i === highlightedIndex
                    ? "bg-teal-50 text-teal-700"
                    : "text-slate-800 hover:bg-slate-50"
                }`}
              >
                {city}
              </li>
            ))
          ) : (
            <li className="px-3.5 py-2.5 text-slate-400 italic">
              {query.trim().length === 0
                ? "Type to search... (English or Tamil)"
                : "No cities found"}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default CitySearchSelect;
